import React, { useEffect, useState } from "react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import {
    Loader2, IdCard, Mail, Phone, MapPin, Briefcase, Clock, DollarSign, Languages, ExternalLink, CheckCircle2, AlertTriangle, FileText, Download,
} from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "@/components/ui/sonner";

const Row = ({ icon: Icon, label, children }) =>
    children ? (
        <div className="flex items-start gap-3 text-sm">
            <Icon size={15} className="text-brand-green mt-0.5 shrink-0" />
            <div>
                <div className="text-[11px] uppercase tracking-wider text-brand-muted">{label}</div>
                <div className="text-brand-navy dark:text-slate-100">{children}</div>
            </div>
        </div>
    ) : null;

export const PassportPreviewDialog = ({ open, onOpenChange, requestId }) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);

    useEffect(() => {
        if (!open || !requestId) return;
        let cancelled = false;
        setLoading(true);
        setData(null);
        api.get(`/admin/passport-requests/${requestId}/preview`)
            .then(({ data }) => { if (!cancelled) setData(data); })
            .catch((err) => { if (!cancelled) toast.error(formatApiError(err)); })
            .finally(() => { if (!cancelled) setLoading(false); });
        return () => { cancelled = true; };
    }, [open, requestId]);

    const c = data?.candidate;
    const m = data?.match;
    const req = data?.request;

    const downloadResume = () => {
        if (!c?.id) return;
        const token = localStorage.getItem("simonara_admin_token");
        // Open in new tab with Authorization is impossible; use the same endpoint via api client and stream to Blob.
        api.get(`/admin/resumes/${c.id}/download`, { responseType: "blob" })
            .then(({ data: blob }) => {
                const url = window.URL.createObjectURL(blob);
                const a = document.createElement("a");
                a.href = url;
                a.download = c.resume_filename || "resume.pdf";
                a.click();
                window.URL.revokeObjectURL(url);
            })
            .catch((err) => toast.error(formatApiError(err)));
        void token;
    };

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent data-testid="passport-preview-dialog" className="max-w-3xl max-h-[85vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <IdCard size={18} className="text-brand-green" /> Talent Passport preview
                    </DialogTitle>
                    <DialogDescription>
                        Qualify candidate + employer fit before reaching out.
                    </DialogDescription>
                </DialogHeader>

                {loading && (
                    <div className="py-10 flex items-center justify-center text-brand-muted text-sm gap-2">
                        <Loader2 size={16} className="animate-spin" /> Loading passport…
                    </div>
                )}

                {!loading && data && (
                    <div className="space-y-6 pt-2">
                        {/* Candidate */}
                        <section>
                            <div className="flex items-center justify-between flex-wrap gap-2">
                                <h3 className="font-heading font-semibold text-lg text-brand-navy dark:text-slate-100">
                                    {c?.name || req?.candidate_name || "Candidate"}
                                </h3>
                                {m?.match_score != null && (
                                    <Badge className="bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 mono-score border border-emerald-500/30">
                                        {m.match_score}% AI Match
                                    </Badge>
                                )}
                            </div>
                            {m?.headline && <p className="text-sm text-brand-muted mt-1">{m.headline}</p>}
                            {!c && (
                                <div className="mt-3 rounded-xl bg-amber-500/10 border border-amber-500/20 p-3 text-xs text-amber-700 dark:text-amber-300">
                                    Candidate record was not found in the resume database — it may have been deleted.
                                </div>
                            )}
                            {c && (
                                <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
                                    <Row icon={Mail} label="Email">{c.email && <a href={`mailto:${c.email}`} className="hover:underline text-brand-green">{c.email}</a>}</Row>
                                    <Row icon={Phone} label="Phone">{c.phone && <a href={`tel:${c.phone}`} className="hover:underline">{c.phone}</a>}</Row>
                                    <Row icon={MapPin} label="City">{c.city}</Row>
                                    <Row icon={Briefcase} label="Desired role">{c.desired_position}</Row>
                                    <Row icon={Clock} label="Years experience">{c.years_experience}</Row>
                                    <Row icon={Clock} label="Availability">{c.availability}</Row>
                                    <Row icon={DollarSign} label="Desired salary">{c.desired_salary}</Row>
                                    <Row icon={Languages} label="Languages">{c.languages_spoken}</Row>
                                    <Row icon={CheckCircle2} label="Work authorization">{c.work_authorization}</Row>
                                    <Row icon={ExternalLink} label="LinkedIn">
                                        {c.linkedin_url && (
                                            <a href={c.linkedin_url} target="_blank" rel="noopener noreferrer" className="hover:underline text-brand-green">
                                                {c.linkedin_url.replace(/^https?:\/\//, "").slice(0, 40)}
                                            </a>
                                        )}
                                    </Row>
                                </div>
                            )}
                        </section>

                        {/* Match reasoning */}
                        {(m?.fit_reasons?.length > 0 || m?.gaps?.length > 0) && (
                            <section className="rounded-xl bg-brand-sand dark:bg-slate-900/40 border border-border p-4">
                                <h4 className="font-heading font-semibold text-sm text-brand-navy dark:text-slate-100">Why Simonara AI matched them</h4>
                                <ul className="mt-3 space-y-1.5">
                                    {(m?.fit_reasons || []).map((r, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs text-brand-navy dark:text-slate-200">
                                            <CheckCircle2 size={13} className="text-emerald-500 mt-0.5 shrink-0" /> {r}
                                        </li>
                                    ))}
                                    {(m?.gaps || []).map((g, i) => (
                                        <li key={i} className="flex items-start gap-2 text-xs text-brand-muted">
                                            <AlertTriangle size={13} className="text-amber-500 mt-0.5 shrink-0" /> Gap: {g}
                                        </li>
                                    ))}
                                </ul>
                            </section>
                        )}

                        {/* Job */}
                        {data.job?.title && (
                            <section>
                                <h4 className="font-heading font-semibold text-sm text-brand-navy dark:text-slate-100">Role: {data.job.title}</h4>
                                <div className="text-xs text-brand-muted mt-1">{[data.job.company, data.job.location, data.job.employment_type, data.job.salary].filter(Boolean).join(" · ")}</div>
                                {data.job.summary && <p className="text-sm text-brand-navy dark:text-slate-200 mt-2 leading-relaxed">{data.job.summary}</p>}
                            </section>
                        )}

                        {/* Resume excerpt */}
                        {c?.resume_excerpt && (
                            <section>
                                <div className="flex items-center justify-between">
                                    <h4 className="font-heading font-semibold text-sm text-brand-navy dark:text-slate-100 flex items-center gap-2">
                                        <FileText size={14} className="text-brand-green" /> Resume excerpt
                                    </h4>
                                    {c.has_resume_file && (
                                        <button
                                            onClick={downloadResume}
                                            data-testid="passport-preview-download-resume"
                                            className="inline-flex items-center gap-1.5 text-xs font-semibold text-brand-green hover:underline"
                                        >
                                            <Download size={12} /> Download PDF
                                        </button>
                                    )}
                                </div>
                                <pre className="mt-2 whitespace-pre-wrap text-xs leading-relaxed text-brand-navy dark:text-slate-200 bg-muted rounded-xl p-4 max-h-64 overflow-y-auto">
                                    {c.resume_excerpt}
                                </pre>
                            </section>
                        )}

                        {/* Employer */}
                        {req && (
                            <section className="rounded-xl bg-brand-navy dark:bg-slate-900 text-white p-4">
                                <div className="text-[11px] uppercase tracking-wider text-white/60">Requested by</div>
                                <div className="mt-1 font-heading font-semibold">{req.company_name || req.employer_name}</div>
                                <div className="text-sm text-white/70">
                                    {req.employer_name} · <a href={`mailto:${req.employer_email}`} className="hover:text-brand-green underline underline-offset-2">{req.employer_email}</a>
                                </div>
                                <div className="text-[11px] text-white/50 mt-1">{req.created_at ? new Date(req.created_at).toLocaleString() : ""}</div>
                            </section>
                        )}
                    </div>
                )}
            </DialogContent>
        </Dialog>
    );
};

export default PassportPreviewDialog;
